import { AuraBaseTokenSections } from '@primeuix/themes/aura/base';

export const semanticTokens: AuraBaseTokenSections.Semantic = {
  primary: {
    50: '{indigo.50}',
    100: '{indigo.100}',
    200: '{indigo.200}',
    300: '{indigo.300}',
    400: '{indigo.400}',
    500: '{indigo.500}',
    600: '{indigo.600}',
    700: '{indigo.700}',
    800: '{indigo.800}',
    900: '{indigo.900}',
    950: '{indigo.950}',
  },
  anchorGutter: '2px',
  iconSize: '0.875rem',
  disabledOpacity: '0.4',
  formField: {
    paddingX: '0.75rem',
    paddingY: '0.5625rem',
    borderRadius: '{border.radius.md}',
    sm: {
      fontSize: '0.875rem',
      paddingX: '0.75rem',
      paddingY: '0.375rem',
    },
    lg: {
      fontSize: '1.125rem',
      paddingX: '0.875rem',
      paddingY: '0.625rem',
    },
    transitionDuration: '{transition.duration}',
  },
  content: {
    borderRadius: '{border.radius.md}',
  },
  focusRing: {
    width: '1',
    style: 'solid',
    color: '{primary.700}',
    offset: '0',
    shadow: 'none',
  },
  list: {
    padding: '0.25rem 0.25rem',
    gap: '2px',
    header: {
      padding: '0.5rem 0.75rem 0.25rem 0.75rem',
    },
    option: {
      padding: '0.5rem 0.75rem',
      borderRadius: '{border.radius.sm}',
    },
    optionGroup: {
      padding: '0.5rem 0.75rem',
      fontWeight: '600',
    },
  },
  navigation: {
    list: {
      padding: '0.5rem',
      gap: '0',
    },
    item: {
      padding: '0.375rem 0.5rem',
      borderRadius: '{border.radius.md}',
      gap: '0.5rem',
    },
    submenuLabel: {
      padding: '0.375rem 0.5rem',
      fontWeight: '600',
    },
    submenuIcon: {
      size: '0.875rem',
    },
  },
  overlay: {
    select: {
      borderRadius: '{border.radius.md}',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    },
    popover: {
      borderRadius: '{border.radius.md}',
      padding: '0.75rem',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    },
    modal: {
      borderRadius: '{border.radius.xl}',
      padding: '1.25rem',
      shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    },
    navigation: {
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    },
  },
  transitionDuration: '0.2s',
  colorScheme: {
    light: {
      surface: {
        0: '#ffffff',
        50: '{slate.50}',
        100: '{slate.100}',
        200: '{slate.200}',
        300: '{slate.300}',
        400: '{slate.400}',
        500: '{slate.500}',
        600: '{slate.600}',
        700: '{slate.700}',
        800: '{slate.800}',
        900: '{slate.900}',
        950: '{slate.950}',
      },
      content: {
        borderColor: '{slate.300}',
        background: '{surface.0}',
      },
      text: {
        color: '{primary.950}',
      },
      formField: {
        iconColor: '{color.main.tertiary}',
      },
      // Figma scheme light colors
      ...{
        color: {
          main: {
            primary: '{primary.950}',
            secondary: '#141140A3',
            tertiary: '#14114066',
            disabled: '#14114066',
            decoration: '#1411400D',
            transparent: 'transparent',
          },
          background: {
            back: '{gray.50}',
            front: '#FFFFFF',
          },
          brand: {
            subtle: '{primary.50}',
            medium: '{primary.100}',
            main: '{primary.500}',
            hover: '{primary.600}',
            focus: '{primary.700}',
            contrast: '#FFFFFF',
          },
          success: {
            subtle: '{green.50}',
            main: '{green.600}',
            hover: '{green.700}',
            focus: '{green.800}',
          },
          fail: {
            subtle: '{red.50}',
            main: '{red.600}',
            hover: '{red.700}',
            focus: '{red.800}',
          },
          warning: {
            subtle: '{orange.50}',
            main: '{orange.600}',
            hover: '{orange.700}',
            focus: '{orange.800}',
          },
          info: {
            subtle: '{violet.50}',
            main: '{violet.600}',
            hover: '{violet.700}',
            focus: '{violet.800}',
          },
          border: {
            main: '{gray.300}',
            hover: '{gray.400}',
            focus: '{primary.500}',
          },
        },
      },
    },
    dark: {
      primary: {
        color: '{primary.300}',
        contrastColor: '{primary.950}',
        hoverColor: '{primary.200}',
        activeColor: '{primary.100}',
      },
      highlight: {
        background: 'rgba(116, 68, 198, 0.16)',
        focusBackground: 'rgba(116, 68, 198, 0.24)',
        color: 'rgba(255,255,255,.87)',
        focusColor: 'rgba(255,255,255,.87)',
      },
      mask: {
        background: 'rgba(0,0,0,0.6)',
        color: '{surface.200}',
      },
      formField: {
        background: '{primary.950}',
        disabledBackground: '#282749',
        disabledColor: '#7F7E92',
        filledBackground: '{primary.950}',
        filledFocusBackground: '{primary.950}',
        filledHoverBackground: '{primary.950}',
        borderColor: '{slate.500}',
        hoverBorderColor: '{slate.500}',
        focusBorderColor: '{primary.300}',
        invalidBorderColor: '{red.300}',
        invalidPlaceholderColor: '{red.300}',
        placeholderColor: '#AAA9BA',
        floatLabelColor: '#AAA9BA',
        floatLabelFocusColor: '#AAA9BA',
        floatLabelActiveColor: '#AAA9BA',
        floatLabelInvalidColor: '{red.300}',
        iconColor: '{color.main.tertiary}',
        shadow: '0 1px 2px 0 rgba(18, 18, 23, 0.05)',
      },
      content: {
        borderColor: '{slate.500}',
        background: '{primary.950}',
        hoverBackground: '{primary.900}',
        hoverColor: '{text.hover.color}',
        color: '{text.color}',
      },
      text: {
        color: 'rgba(255, 255, 255, 0.9)',
        hoverColor: '{slate.50}',
        hoverMutedColor: '{surface.300}',
        mutedColor: '{surface.400}',
      },
      overlay: {
        select: {
          background: '{surface.900}',
          borderColor: '{surface.700}',
          color: '{text.color}',
        },
        popover: {
          background: '{primary.950}',
          borderColor: '{slate.500}',
          color: '{text.color}',
        },
        modal: {
          background: '{primary.950}',
          borderColor: '{surface.500}',
          color: '{text.color}',
        },
      },
      list: {
        option: {
          focusBackground: '{primary.950}',
          focusColor: '{text.hover.color}',
          selectedBackground: '{primary.900}',
          selectedFocusBackground: '{primary.800}',
          selectedFocusColor: '{highlight.focus.color}',
          selectedColor: '{highlight.color}',
          color: '{text.color}',
          icon: {
            color: '#72708C',
            focusColor: '#72708C',
          },
        },
        optionGroup: {
          background: 'transparent',
          color: '{text.muted.color}',
        },
      },
      navigation: {
        item: {
          focusBackground: '{surface.800}',
          focusColor: '{text.hover.color}',
          activeBackground: '{surface.800}',
          activeColor: '{text.hover.color}',
          color: '{text.color}',
          icon: {
            color: '{surface.500}',
            focusColor: '{surface.400}',
            activeColor: '{surface.400}',
          },
        },
        submenuLabel: {
          background: 'transparent',
          color: '{text.muted.color}',
        },
        submenuIcon: {
          color: '{surface.500}',
          focusColor: '{surface.400}',
          activeColor: '{surface.400}',
        },
      },
      // Figma scheme dark colors
      ...{
        color: {
          main: {
            primary: '#FFFFFFE6',
            secondary: '#FFFFFFA3',
            tertiary: '#FFFFFF66',
            disabled: '#FFFFFF66',
            decoration: '#FFFFFF1A',
            transparent: 'transparent',
          },
          background: {
            back: '#100E34',
            front: '{primary.950}',
          },
          brand: {
            subtle: '{primary.900}',
            medium: '{primary.800}',
            main: '{primary.300}',
            hover: '{primary.200}',
            focus: '{primary.100}',
            contrast: '{primary.950}',
          },
          success: {
            subtle: '{green.900}',
            main: '{green.300}',
            hover: '{green.200}',
            focus: '{green.100}',
          },
          fail: {
            subtle: '{red.900}',
            main: '{red.300}',
            hover: '{red.200}',
            focus: '{red.100}',
          },
          warning: {
            subtle: '{orange.900}',
            main: '{orange.300}',
            hover: '{orange.200}',
            focus: '{orange.100}',
          },
          info: {
            subtle: '{violet.900}',
            main: '{violet.300}',
            hover: '{violet.200}',
            focus: '{violet.100}',
          },
          border: {
            main: '{gray.600}',
            hover: '{gray.500}',
            focus: '{primary.300}',
          },
        },
      },
    },
  },
  ...{
    slon: '{red.500}',
  },
};
